import * as React from 'react';
import type { IColumnsLayoutProps } from './types';
import { cn } from '@utils';

/** Tailwind gap multiplier: 1 unit = 0.25rem */
const GAP_MULTIPLIER = 0.25;

/**
 * ColumnsLayout - A flexible grid layout component that distributes children by specified ratios.
 *
 * - With ratios: Uses grid with specified column ratios
 * - Without ratios: Defaults to evenly distributed columns
 *
 * For centered single-child layouts, use MiddleColumnLayout instead.
 */
const ColumnsLayout = React.forwardRef<HTMLDivElement, IColumnsLayoutProps>(
	({ children, ratios, gap = 4, className, style, ...props }, ref) => {
		const childrenCount: number = React.Children.count(children);

		const gridTemplateColumns: React.CSSProperties['gridTemplateColumns'] = ratios?.length
			? ratios.map((ratio: number) => `${ratio ?? 1}fr`).join(' ')
			: Array(childrenCount).fill('1fr').join(' ');

		const gridStyle: React.CSSProperties = {
			gridTemplateColumns,
			gap: `${gap * GAP_MULTIPLIER}rem`,
			...style,
		};

		return (
			<div
				ref={ref}
				className={cn('grid h-full w-full', className)}
				style={gridStyle}
				data-aid="columns-layout"
				{...props}
			>
				{children}
			</div>
		);
	}
);

ColumnsLayout.displayName = 'ColumnsLayout';

export { ColumnsLayout };
